import React, { FC, useEffect } from 'react';
import _ from 'lodash';
import { Box } from '@wix/design-system';
import FilterTags from './FilterTags';
import GalleryGrid from './GalleryGrid';
import {
  APP_STATUS,
  useTemplatesContext,
} from '@components/Contexts/TemplatesContext';
import PaginationFooter from '@components/PaginationFooter';
import { templatesPath, useShallowRouter } from '@components/Hooks/useRouter';
import { updateQueryParams } from '@utils/route';
import { useTemplatesQuery } from '@components/Hooks/useTemplatesQuery';
import { useRouter } from 'next/router';
import { useTemplateGalleryFilters } from '@components/Hooks/useTemplateGalleryFilters/useTemplateGalleryFilters';
import NoResultsErrorState from './../ErrorStates/NoResultsErrorState';
import GenericErrorState from './../ErrorStates/GenericErrorState';
import { useTemplatesBi } from '@components/Hooks/useTemplatesBi';
import s from './TemplateGallery.module.scss';
import galleryGridStyle from './GalleryGrid/GalleryGrid.module.scss';
import GalleryHeader from './GalleryHeader';
import TemplateCardListItems from './TemplateCardListItems';
import TemplateCardAdaptive from './TemplateCard/TemplateCardAdaptive';
import TemplateCardListItemsLoadingState from './TemplateCardListItems/TemplateCardListItemsLoadingState';

function ErrorState({ showEmptyError }: { showEmptyError?: Boolean }) {
  const { navigate, router } = useShallowRouter();

  const clearAll = () => {
    navigate(
      updateQueryParams({}, router, {
        all: true,
      }),
      false,
    );
    setTimeout(() => window.location.reload(), 0);
  };

  return showEmptyError ? (
    <NoResultsErrorState />
  ) : (
    <GenericErrorState overrideAction={clearAll} />
  );
}

const TemplateGallery: FC = () => {
  const {
    state: { filters, currentPage, sort, appStatus, filterOpen, SSRFlow },
  } = useTemplatesContext();
  const router = useRouter();
  const { hasActiveFilters } = useTemplateGalleryFilters();
  const tagPaddingClassName = hasActiveFilters ? '' : s.noTagsPadding;

  const { data } = useTemplatesQuery(filters, currentPage, sort);

  useEffect(() => {
    window && window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [data]);

  const moreThanOnePage = data?.numberOfPages && data?.numberOfPages > 1;
  const { logBiClick } = useTemplatesBi({
    is_mobile: false,
  });
  const getPageUrl = (page: number) =>
    `${templatesPath}${updateQueryParams({ page }, router)}`;

  const onPageClicked = (page: number) => {
    logBiClick({
      click_type: 'Next page',
      click_data: { page_number: page },
    });
  };

  const noTemplatesForPage =
    _.size(data?.templates) <= 0 && (data?.count ?? 0 > 0);
  const showGeneralError = appStatus === APP_STATUS.ERROR || noTemplatesForPage;
  const showEmptyError = data?.count === 0;

  return (
    <Box
      dataHook="templates-gallery-container"
      height="100%"
      direction="vertical"
      paddingBottom="152px"
      borderLeft={'1px solid var(--wsr-color-D60, #DFE5EB)'}
    >
      {<FilterTags />}
      {showEmptyError || showGeneralError ? (
        <Box height="calc(100% - 171px)">
          <ErrorState showEmptyError={showEmptyError} />
        </Box>
      ) : (
        <Box
          dataHook="templates-gallery"
          direction="vertical"
          minHeight="calc(100vh - 230px)"
          className={`${s.templateGalleryContainer} ${tagPaddingClassName}`}
        >
          <GalleryGrid
            header={<GalleryHeader templateCount={data?.count} />}
            listClassName={filterOpen ? '' : galleryGridStyle.filterClosed}
            content={
              <TemplateCardListItems
                CardComponent={TemplateCardAdaptive}
                LoadingState={TemplateCardListItemsLoadingState}
                SSRFlow={SSRFlow}
                templates={data?.templates}
                isMobileView={false}
              />
            }
          />
          {
            <>
              {moreThanOnePage && (
                <PaginationFooter
                  currentPage={currentPage}
                  numberOfPages={data?.numberOfPages ?? 0}
                  getPageUrl={getPageUrl}
                  onPageClicked={onPageClicked}
                  showTopDivider={false}
                />
              )}
            </>
          }
        </Box>
      )}
    </Box>
  );
};

export default TemplateGallery;
